import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Dialog, { Clip } from '../molecules/Dialog';
import Button from '../atoms/Button.Container';
import BackButton from '../molecules/BackButton.Container';
import Text from '../atoms/Text';
import Image from '../atoms/Image';
import type { Concept } from '../../data/Concepts';
import assets, { getImageByKey } from '../../assets/Assets';
import SpeechBubble from '../atoms/SpeechBubble';
import AutofitText from '../molecules/AutofitText';
import PersonaAnimation from '../../assets/styles/Persona.module.css';

const Header = styled.div`
	background-color: ${(props) => props.theme.colors.veryLightBlue};
	color: ${(props) => props.theme.colors.veryDarkBlue};
	height: 55%;
	overflow: hidden;
`;

const Title = styled.span`
	display: inline-block;
	font: ${(props) => props.theme.font.title};
	font-size: 9vmin;
	left: calc(6% + 35vmin - 2%);
	overflow: hidden;
	position: absolute;
	right: calc(6% + 35vmin - 2%);
	text-overflow: ellipsis;
	top: 8%;
	white-space: nowrap;
`;

const Description = styled.div`
	font: ${(props) => props.theme.font.default};
	font-size: 3vmin;
	font-weight: bold;
	left: calc(6% + 35vmin - 2%);
	position: absolute;
	right: calc(6% + 35vmin - 2%);
	top: calc(8% + 9vmin + 4%);
`;

const Icon = styled.div`
	color: ${(props) => props.theme.colors.black};
`;

const ProArgument = styled.div`
	color: ${(props) => props.theme.colors.darkBlue};
	font-weight: bold;
`;

const ContraArgument = styled.div`
	color: ${(props) => props.theme.colors.veryDarkRed};
	font-weight: bold;
`;

export interface StateProps {
	activated: boolean;
}

export interface DispatchProps {}

export interface OwnProps {
	concept: Concept;
	onActivate: (concept: Concept) => void;
	onClose: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const ConceptDetailsDialog: FunctionComponent<Props> = (
	props: PropsWithChildren<Props>
) => {
	const { activated, concept, onActivate, onClose, children } = props;
	const { t } = useTranslation();

	return (
		<Dialog>
			<Clip>
				<Header>
					<Title>
						<Trans tOptions={{ returnObjects: true }}>
							{concept.texts.name}
						</Trans>
					</Title>
					<Description>
						<Text
							content={t(concept.texts.description, { returnObjects: true })}
						/>
					</Description>
				</Header>
				{/* Proponent */}
				<Image
					className={PersonaAnimation.left}
					src={assets.images['proponent.body']}
					width="35vmin"
					height="50vmin"
					position="absolute"
					left="6%"
					bottom="0"
				/>
				<SpeechBubble
					anchor="left_bottom"
					position="absolute"
					height="30%"
					left="calc(6% + 35vmin - 2%)"
					right="49%"
					bottom="26%"
					fadeInDuration={300}
					fadeInDelay={1_000}
				>
					<AutofitText>
						<ProArgument>
							<Text
								content={t(concept.texts.proArgument, { returnObjects: true })}
							/>
						</ProArgument>
					</AutofitText>
				</SpeechBubble>
				{/* Opponent */}
				<Image
					className={PersonaAnimation.right}
					src={assets.images['opponent.body']}
					width="35vmin"
					height="50vmin"
					position="absolute"
					right="6%"
					bottom="0"
				/>
				<SpeechBubble
					anchor="right_top"
					position="absolute"
					height="30%"
					left="49%"
					right="calc(6% + 35vmin - 2%)"
					bottom="4%"
					fadeInDuration={300}
					fadeInDelay={2_000}
				>
					<AutofitText>
						<ContraArgument>
							<Text
								content={t(concept.texts.contraArgument, {
									returnObjects: true
								})}
							/>
						</ContraArgument>
					</AutofitText>
				</SpeechBubble>
			</Clip>
			{/* Actions */}
			<Icon>
				<Image
					src={getImageByKey(concept.images.icon)}
					width="26vmin"
					height="26vmin"
					position="absolute"
					right="6%"
					top="4%"
				/>
			</Icon>
			{activated ? undefined : (
				<Button
					primary
					disabled={activated}
					onClick={() => onActivate(concept)}
					width="calc(26vmin - 4%)"
					position="absolute"
					right="calc(6% + 2%)"
					top="calc(4% + 26vmin + 2%)"
					highlighted
					highlightDelay="long"
				>
					<Trans>concept.activate</Trans>
				</Button>
			)}
			<BackButton onClick={onClose} position="absolute" left="-1%" top="16%">
				<Trans>concept_details.back</Trans>
			</BackButton>
			{children}
		</Dialog>
	);
};

export default ConceptDetailsDialog;
